import React, { useState, useEffect } from 'react';
import { useGlobal } from '@rcg/ui-react';
import Modal from '@material-ui/core/Modal';
import { navigate } from '@gatsbyjs/reach-router';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import Dictionary from '../../utils/dictionary';
import useStyles from './useStyles';
import Button from '../UiComponents/Button';
import AirLoader from '../AirLoader';
import { setSessionStorageItem } from '../../operations/sessionStorage/utils';
import {
  NAVIGATION_PATHS,
  SESSION_STORAGE_AMEND_ORIGIN,
  SESSION_STORAGE_BOOKING_PROFILE_NAME,
  SESSION_STORAGE_LAST_BOOKED_RESERVATION,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_HELD_RESERVATION,
  SESSION_STORAGE_LAST_PAYMENT,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_SEARCH_PARAMS,
  SESSION_STORAGE_LAST_SELECTED_DEPARTURE_FLIGHT,
  SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT,
} from '../../utils/constants';
import { getGuests } from '../../utils/payment';
import { getManageRedirectionUrl } from '../../utils/common';
import useExtendHoldSession from '../../operations/dataAccess/useExtendHoldSession';
import {
  heldReservationVar,
  prefixPathVar,
  searchParamsVar,
  bookingProfileVar,
} from '../../operations/localState/reactiveVariables';

const ExtendSessionModal = ({ forceOpen = false, sixMin = 360000, oneMin = 60000 }) => {
  const classes = useStyles();
  const {
    data: { lang, market },
    renderContent,
  } = useGlobal();
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clickedContinueTimeOut, setClickedContinueTimeOut] = useState(false);
  const heldReservation = heldReservationVar();

  const handleClose = () => {
    setOpen(false);
  };

  const handleBookLaterButton = () => {
    setSessionStorageItem(SESSION_STORAGE_BOOKING_PROFILE_NAME, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_SELECTED_DEPARTURE_FLIGHT, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_SELECTED_RETURN_FLIGHT, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_HELD_RESERVATION, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_BOOKED_RESERVATION, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_SEARCH_PARAMS, null);
    setSessionStorageItem(SESSION_STORAGE_LAST_PAYMENT, null);
    const isAmendOrigin = sessionStorage.getItem(SESSION_STORAGE_AMEND_ORIGIN) || false;
    window.location.assign(
      isAmendOrigin
        ? getManageRedirectionUrl()
        : `${prefixPathVar()}${NAVIGATION_PATHS.base}${NAVIGATION_PATHS.bookFlights}`,
    );
  };

  const [extendHoldSession] = useExtendHoldSession({
    onCompleted: () => {
      handleClose();
      if (count !== 1) {
        setCount(0);
      }
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
      handleBookLaterButton();
    },
  });

  const callExtendHoldSessionQuery = () => {
    const guests = getGuests(bookingProfileVar, searchParamsVar);
    const bookingProfile = bookingProfileVar();
    extendHoldSession({
      variables: {
        input: {
          correlationID: heldReservation.holdFlight.correlationID,
          hashKey: heldReservation.associatedHashKey,
          guests,
          bookingID: bookingProfile?.currentBooking?.id,
        },
      },
    });
  };

  const handleContinueButton = () => {
    setLoading(true);
    callExtendHoldSessionQuery();
    clearTimeout(clickedContinueTimeOut);
  };

  useEffect(() => {
    let timeout;
    // After 6 minutes, FE fires off an extend automatically.
    if (count === 0) {
      timeout = setTimeout(() => {
        callExtendHoldSessionQuery();
        setCount(count + 1);
      }, sixMin);
    }
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line
  }, [count]);

  useEffect(() => {
    let timeout;
    if (count === 1) {
      // And then at 12 minutes, the modal appears asking if they want to extend.
      timeout = setTimeout(() => {
        setOpen(true);
        setCount(count + 1);
      }, sixMin);
    }
    if (count === 2) {
      // After the 1 minute without user interaction, the user will be redirected to the search page.
      timeout = setTimeout(() => {
        navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
      }, oneMin);
      setClickedContinueTimeOut(timeout);
    }
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line
  }, [count]);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      callExtendHoldSessionQuery();
    }
  }, [forceOpen]);
  return (
    <>
      <AirLoader open={loading} />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Extend Session"
        aria-describedby="Extend Session"
        disableBackdropClick
        disableEscapeKeyDown
        className={classes.extendSessionModal}>
        <div className={classes.extendSessionBody}>
          <div className={classes.extendSessionTitle}>
            {renderContent(
              Dictionary.airExtendSessionTitle.key,
              Dictionary.airExtendSessionTitle.defaultValue,
            )}
          </div>
          <div className={classes.extendSessionContent}>
            {renderContent(
              Dictionary.airExtendSessionText.key,
              Dictionary.airExtendSessionText.defaultValue,
            )}
          </div>
          <div className={classes.extendSessionFooter}>
            <Button
              data-testid="extend-session-book-later-btn"
              onClick={handleBookLaterButton}
              variant="contained"
              color="primary">
              {renderContent(
                Dictionary.airExtendSessionBookLaterButton.key,
                Dictionary.airExtendSessionBookLaterButton.defaultValue,
              )}
            </Button>
            <Button
              data-testid="extend-session-continue-btn"
              className={classes.submitbutton}
              onClick={handleContinueButton}
              variant="contained"
              color="primary">
              {renderContent(
                Dictionary.airExtendSessionContinueButton.key,
                Dictionary.airExtendSessionContinueButton.defaultValue,
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ExtendSessionModal;
